import React from "react";
import PropTypes from "prop-types";
import {AButton} from "@cisco-sbg-ui/magna-react";
import {appConfig} from "config/defaultConfig";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  TextSection
} from "components/Drawer";
import {setDrawerIncident} from "store/features/incidentsSlice";
import {selectDrawerIncident} from "store/selectors";
import {sendViewIncidentDetailsButtonTelemetry} from "api/telemetry/incidentsTelemetrics";
import {getShortId} from "utils/misc";
import IncidentDrawerHeader from "./IncidentDrawerHeader";
import PriorityScoreSection from "./PriorityScoreSection";
import css from "./IncidentDrawer.module.scss";

const IncidentDrawer = ({onClose, isOpen, ...rest}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {ROUTES} = appConfig.APP;
  const incident = useSelector(selectDrawerIncident);
  // Ensures that the drawer will close if the incident is deleted
  const open = isOpen && incident !== null;

  const handleClose = () => {
    // Wait for close animation to finish before clearing the
    // incident drawer data in order to prevent a period of
    // time where the drawer appears empty.
    onClose().then(() => dispatch(setDrawerIncident(null)));
  };

  return (
    <Drawer isOpen={open} onClose={handleClose} position="fixed" {...rest}>
      {incident && (
        <>
          <DrawerHeader className={css.incidentHeader}>
            <IncidentDrawerHeader incident={incident} />
          </DrawerHeader>

          {/**
           * We have a `key` on the `DrawerBody` so that when a new incident
           * is opened in the drawer, we properly clean-up the state inside the
           * previously opened drawer's `DrawerBody`, e.g, opened accordions,
           * typed checkboxes, etc.
           *
           * For a demonstration, remove the `key` prop, then collapse an incident's
           * description, and open another incident. The accordion stays collapsed.
           *
           * @see https://beta.reactjs.org/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
           */}
          <DrawerBody key={incident?.id}>
            <PriorityScoreSection {...{incident}} />
            <TextSection
              text={
                incident?.short_description?.trim().length > 0
                  ? incident?.short_description
                  : null
              }
              title={t("drawers.labels.common.shortDescription")}
            />
            <TextSection
              text={incident?.description}
              title={t("drawers.labels.common.longDescription")}
            />
          </DrawerBody>

          <DrawerFooter>
            <AButton
              component={Link}
              to={`${ROUTES.INCIDENTS}/${getShortId(incident.id)}`}
              medium
              onClick={() => {
                const {id, severity, short_description, source, status, title} =
                  incident;

                sendViewIncidentDetailsButtonTelemetry(
                  `${getShortId(id)}`,
                  severity,
                  short_description,
                  source,
                  status,
                  title
                );
              }}>
              {t("incidentDetail.actions.viewIncidentDetail")}
            </AButton>
          </DrawerFooter>
        </>
      )}
    </Drawer>
  );
};

const {...DrawerProps} = Drawer.propTypes;

IncidentDrawer.propTypes = {
  ...DrawerProps,

  /**
   * A function that returns a promise to indicate when
   * the drawer is closed. This is required in order to
   * confidently reset the data within the IncidentDrawer
   * only until it has finished its closing animation.
   */
  onClose: PropTypes.func
};

export default IncidentDrawer;
